import { useContact } from "@/OneLiners";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useAppEnum,
    useFormLifeCycle
} from "@/Shared";

import { ContactFormViewModel } from "../ViewModels";
import { useContactEnricher } from "../Hooks";
import { ContactValidationSchema } from "../ContactFormValidation";
import { ContactModel } from "../ContactModel";


/**
 * useContactForm hook – orchestrates contact form initialization and submission.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Coordinates permissions, domain state and enrichment layers
 * - Builds and exposes the ContactFormViewModel to the UI
 * - Delegates submission logic and feedback handling to dedicated hooks
 * 
 * Design notes:
 * - This hook does not perform CRUD or permission resolution directly
 * - It acts as an orchestration layer between UI and domain-specific hooks
 */
export const useContactForm = (props: DomainHookProps): DomainFormHook => {
    const { permissionHook } = props;
    const { domainType } = DOMAIN_CONFIGS.Contact;

    /**
     * Contact domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const contactHook = useContact();


    /**
     * Contact-related enums exposed to the UI layer.
     * Used to build the form view model.
     */
    const { ContactEnums } = useAppEnum();

    /**
     * Contact enrichment layer.
     * Provides additional data (users, computed fields, etc.)
     * required to build the form view model.
     */
    const {
        userMap,
        enricherResponse,
        getEnrichedContact,
    } = useContactEnricher();

    /**
     * Aggregated response representing the global initialization state.
     * Combines:
     * - contact domain state
     * - permission loading state
     * - enrichment layer state
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            contactHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: contactHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: contactHook,
        buildVM: (currentEntity: ContactModel) => {
            return new ContactFormViewModel(
                ContactEnums,
                userMap,
                getEnrichedContact(currentEntity)
            )
        }
    });


    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: ContactEnums,
        ValidationSchema: ContactValidationSchema(ContactEnums),
    }
}